import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Typography, Box, Alert } from "@mui/material";
import EditProfileForm from "../components/EditProfileForm";

interface OnboardingPageProps {
  userProfile: {
    id: string;
    email?: string;
    name?: string;
  };
  refreshProfile: () => Promise<void>;
}

const OnboardingPage: React.FC<OnboardingPageProps> = ({
  userProfile,
  refreshProfile,
}) => {
  const navigate = useNavigate();
  const [profileSaved, setProfileSaved] = useState(false);

  const handleSaveSuccess = async () => {
    await refreshProfile(); // Refresh the profile state
    setProfileSaved(true);
    navigate("/vote"); // Redirect immediately
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        p: 0,
        overflow: "hidden",
        backgroundColor: "#f8f8f8",
        "& ::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "& ::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "4px",
        },
        "& ::-webkit-scrollbar-thumb": {
          background: "rgba(51, 51, 51, 0.2)",
          borderRadius: "4px",
        },
        "& ::-webkit-scrollbar-thumb:hover": {
          background: "rgba(51, 51, 51, 0.4)",
        },
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(51, 51, 51, 0.2) #f1f1f1",
        transition: "all 150ms ease",
      }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderBottom: 1,
          borderColor: "rgba(51, 51, 51, 0.2)",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            color: "#333333",
            fontWeight: 300,
            letterSpacing: "0.025em",
            fontSize: { xs: "1.5rem", sm: "1.875rem", md: "2.25rem" },
            mb: 1,
            textTransform: "lowercase",
          }}
        >
          complete your profile.
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "rgba(51, 51, 51, 0.7)",
            fontWeight: 300,
            mb: 2,
            fontSize: { xs: "0.875rem", sm: "1rem" },
            textTransform: "lowercase",
          }}
        >
          we currently require all users to create profiles before voting and
          viewing the leaderboard
        </Typography>
        {profileSaved && (
          <Alert
            severity="success"
            sx={{
              mt: 2,
              mb: 2,
              borderRadius: "0.375rem",
              border: "1px solid rgba(51, 51, 51, 0.2)",
              fontWeight: 300,
              transition: "all 150ms ease",
              textTransform: "lowercase",
            }}
          >
            profile saved successfully!
          </Alert>
        )}
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <EditProfileForm
          userProfile={userProfile}
          onSaveSuccess={handleSaveSuccess}
        />
      </Box>
    </Container>
  );
};

export default OnboardingPage;